import React from 'react';
import { a, rem } from 'design-system/values';
import IconGraph from 'design-system/icons/IconGraph';
import IconLeaf from 'design-system/icons/IconLeaf';
import IconRepeat from 'design-system/icons/IconRepeat';
import IconTechBold from 'design-system/icons/IconTechBold';
import IconHomeBold from 'design-system/icons/IconHomeBold';
import { IconMoonBold } from 'design-system/icons/IconMoonBold';
import IconTransaction from 'design-system/icons/IconTransaction';
import IconShoppingBags from 'design-system/icons/IconShoppingBags';
import { IconCoins, IconHeartBeat, IconMine } from 'design-system/icons';
import Text from 'design-system/Text';
import { SelectedPositionFilter, } from './types';
export const performanceExample = {
    title: 'Performance of £10,000 invested on 1 January 2021',
    graphDisclaimer: 'Returns are shown in pounds sterling (GBP). Because iShares Core S&P 500 invests in US equities, changes in exchange rates may affect the value of your investment. Tax treatment depends on your personal circumstances and may change in future. This information is for illustrative and educational purposes only and does not constitute personal investment advice or a recommendation to buy or sell any investment. If you are unsure, seek regulated financial advice.',
    aboutParagraphs: [
        'The key difference between saving and investing is time and growth potential.',
        'While keeping your money in a savings account offers stability, investing gives your money more opportunity to grow, even accounting for market ups and downs.',
        'The cash line reflects the return of a cash ISA earning interest in line with the Bank of England Base Rate, with interest credited monthly.',
        "The investment line shows the performance of an investment ISA holding the iShares Core S&P 500, an accumulating fund that tracks the S&P 500 index, with the ETF's ongoing charges deducted from returns.",
        'The comparison is presented in pounds sterling and reflects the historical performance of each option over this period.',
        'Past performance is not a reliable guide to future returns. The value of your investments can go down as well as up, and you may get back less than you put in.',
        'Data sources: BlackRock and the Bank of England.',
    ],
};
export const ORDER_UPDATE_TIME_LIMIT = 10000; // ms
export const ORDER_UPDATE_INTERVAL = 1000; // ms
export const ACCOUNT_UPDATE_INTERVAL_DEFAULT = 5 * 60 * 1000; // ms, used when current trading account status is "good" (doesn’t need faster updating)
export const ACCOUNT_UPDATE_INTERVAL_FAST = 1 * 60 * 1000; // ms, used when current trading account status implies user to wait for status update
export const PORTFOLIO_UPDATE_MIN_DELAY = 10 * 60 * 1000; // ms, sets minimum delay between updates. Updates happen when app is restored from background/inactive state
export const SHARE_QUANTITY_DECIMALS = 4;
export const ENABLE_QUICK_TOP_UP_OPTIONS = false;
export const STOCK_ICON_SIZE = 44; // with inner border of 1
export const ITEMS_IN_COLLAPSED_SECTION = 3;
export const MIN_TOP_UP_AMOUNT = 1;
export const MIN_WITHDRAW_AMOUNT = 0;
export const MIN_BUY_AMOUNT = 1; // WARNING: if changing to other than 1, please implement logic in MoneyInput or BuyCustomAmountScreen
export const MAX_CHART_RETRIES = 2; // total of 3 attempts max
export const TRADING_CHART_DEFAULT_PERIOD = '1D';
export const PORTFOLIO_CHART_DEFAULT_PERIOD_SECCL = 'MAX';
export const PORTFOLIO_CHART_DEFAULT_PERIOD = '1M';
export const RECENTLY_VIEWED_MAX_COUNT = 10;
export const WITHDRAW_POLL_INTERVAL = 5 * 1000;
export const WITHDRAW_POLL_ATTEMPTS = 3;
export const VIEW_FINDER_TOP = rem(a(160, 160, 160, 200, 200));
export const VIEW_FINDER_HEIGHT = rem(216);
export const VIEW_FINDER_TOP_OFFSET = rem(80);
/* URLs to various documents */
/**
 * Also referred to as "Broker Agreement" and "Account Agreement"
 */
export const ALPACA_CUSTOMER_AGREEMENT = 'https://files.alpaca.markets/disclosures/library/AcctAppMarginAndCustAgmt.pdf';
export const ORDER_EXECUTION_POLICY = 'https://emma-app.com/order-execution-policy';
export const RISK_DISCLOSURE = 'https://emma-app.com/risk-disclosure';
export const TRADING_TERMS = 'https://emma-app.com/trading-terms';
export const LEGAL_NOTICE = 'Emma Technologies Ltd is an appointed representative of RiskSave Technologies Ltd which is authorised and regulated by the Financial Conduct Authority (FRN 775330)';
export const SECCL_TRADING_TERMS = 'https://emma-app.com/seccl-account-terms';
export const SECCL_CUSTODIAN_TERMS = 'https://emma-app.com/seccl-custody-terms';
export const SECCL_ORDER_EXECUTION_POLICY = 'https://emma-app.com/seccl-order-execution-policy';
export const SECCL_RISK_DISCLOSURE = 'https://emma-app.com/seccl-risk-disclosure';
export const SECCL_JISA_TERMS = 'https://emma-app.com/seccl-jisa-terms';
export const SECCL_JISA_DECLARATION = 'https://emma-app.com/seccl-jisa-declaration';
export const SECCL_JISA_KEY_FEATURES = 'https://emma-app.com/seccl-jisa-key-features';
export const SECCL_ISA_TERMS = 'https://emma-app.com/seccl-isa-terms';
export const SECCL_ISA_DECLARATION = 'https://emma-app.com/seccl-isa-declaration';
export const SECCL_ISA_KEY_FEATURES = 'https://emma-app.com/seccl-isa-key-features';
export const SECCL_CASH_ISA_KEY_FEATURES = 'https://emma-app.com/seccl-cash-isa-key-features';
export const SECCL_CASH_ISA_DECLARATION = 'https://emma-app.com/seccl-cash-isa-declaration';
/* Portfolio customizations */
export const ANIMATE_PORTFOLIO_VALUE = true;
export const PORTFOLIO_RESAMPLE_RANGE = 100;
export const CALCULATE_PORTFOLIO_VALUE_BASED_ON_POSITIONS = true;
export const ENABLE_STOCK_RESAMPLE = false;
export const STOCK_RESAMPLE_RANGE = 100;
/**
 * When this toggle is on, the stock chart will be automatically switching to the next period when selected/default period is failed to be retrieved from backend.
 */
export const ENABLE_STOCK_CHARTS_AUTO_SWITCHING_PERIODS = false;
/* Style config (metrics - paddings, margins) */
export const ROW_ITEM_VERTICAL_PADDING = 16;
export const ROW_ITEM_HORIZONTAL_PADDING = 16;
/* Onboarding customizations */
export const MIDDLE_NAME_REQUIRED = false;
export const ENABLE_CHECK_ALL = false;
export const USE_LINK_TO_CUSTOMER_AGREEMENT_ON_SIGNATURE = true;
export const CAN_SKIP_TO_SIGNATURE_IF_WAS_ALREADY_THERE = false;
export const SCROLL_Y_STATUS_BAR_HIDDEN_THRESHOLD = 40;
/**
 * Maximum file size in megabytes for the images which user uploads for KYC checks.
 */
export const KYC_MAX_UPLOAD_MB = 5 * 0.9; // 5MB reduced by 10% as a safety margin
export var OnboardingCardPosition;
(function (OnboardingCardPosition) {
    OnboardingCardPosition[OnboardingCardPosition["TOP"] = 0] = "TOP";
    OnboardingCardPosition[OnboardingCardPosition["BOTTOM"] = 1] = "BOTTOM";
})(OnboardingCardPosition || (OnboardingCardPosition = {}));
export const POSITION_FILTER_OPTIONS = [
    { label: 'Total value', value: SelectedPositionFilter.TOTAL_VALUE, icon: <IconTransaction /> },
    { label: 'Gain / loss', value: SelectedPositionFilter.GAIN_LOSS, icon: <IconRepeat /> },
    { label: 'Return %', value: SelectedPositionFilter.RETURN_PERCENT, icon: <IconGraph /> },
];
/* Stock screen customizations */
export const STOCK_NEWS_SECTION_LIMIT = 10;
/* Stock news customizations */
export const USE_SAFARI_VIEW_OR_CHROME_TABS_FOR_NEWS = true;
/* Linking account customizations */
export const SHOW_CHANGE_ACCOUNT_VIA_SUPPORT_WARNING = false;
export const ALL_STOCKS_KEY = 'ALL_STOCKS';
export const fxConversion = {
    from: 'USD',
    to: 'GBP',
    rate: 1.2687671812222456,
    base: 1.2674,
    fee: {
        value: 0.05,
        currency: 'GBP',
    },
    fromAmount: 60,
    toAmount: 47.29,
    pair: 'GBPUSD',
    rateFrom: 'GBP',
    rateTo: 'USD',
};
export const stockScreenDefaultEventTypes = [
    'SPLIT',
    'MERGER',
    'DIVIDEND',
    'EX_DIVIDEND',
    'SYMBOL_CHANGE',
    'LISTING_CHANGE',
];
/**
 * these are the measurements for the stock graphs
 */
export const CHART_HEIGHT = rem(272);
export const MAX_MIN_INDICATOR_VIEW_HEIGHT = rem(34);
export const LINE_CHART_WRAPPER_HEIGHT = CHART_HEIGHT - rem(12);
export const ACTUAL_CHART_HEIGHT = LINE_CHART_WRAPPER_HEIGHT - rem(34 * 2);
export const ACTUAL_CANDLE_CHART_HEIGHT = CHART_HEIGHT - rem(34 * 2) - rem(21 + 4);
export const BUY_SELL_INDICATOR_VERTICAL_PADDING = MAX_MIN_INDICATOR_VIEW_HEIGHT;
export const STOCK_DEFAULT_CHART = 'line';
export const INVEST_DISCOVER_LIKE_FIRST_SCREEN_SYMBOLS = [
    'TSLA',
    'AMZN',
    'AAPL',
    'GOOG',
    'META',
    'NFLX',
    'MSTR',
    'NVDA',
    'RDDT',
    'SPOT',
    'KO',
];
export const commonToolTipStrings = {
    commissionTitle: 'Commission',
    commissionDesc: 'We don’t charge any commission on trades. We also don’t charge any regulatory fees, such as SEC and FINRA, or ADR fees. We cover for all these.',
    noOfSharesTitle: 'How is this estimated?',
    commissionDescSeccl: 'No commission here. What you invest is exactly what gets invested.',
    noOfSharesDesc: 'This is an estimated number of shares based on the latest data for share prices ad FX rates that we’ve received. The quantity you actually get may be different from the one you see here.',
    noOfSharesWithoutFxFeeDesc: 'This is an estimated number of shares based on the last close price. The quantity you actually receive may differ slightly from what’s shown here.',
    tradingChargeTitle: 'Trading charge',
    tradingChargeDesc: 'This is the dealing fee you would normally pay when you buy or sell an asset. On Emma there are no trading charges.',
    ongoingChargeTitle: 'Ongoing charges figure',
    ongoingChargeDesc: `This charge is set by the fund manager. It covers the day-to-day cost of running the fund. More information can be found in the 'Key information document’.\n\nYou pay this charge for as long as you have the investment. It is calculated daily and charged monthly, the amount is taken from the money you have already invested.`,
    transactionCostTitle: 'Transaction cost',
    transactionCostDesc: `When a fund manager buys or sells assets within a fund (rebalancing), transaction costs are expenses incurred by these trading activities. This cost can vary so the fund manager makes an estimate.\n\nWhilst shown as a cost, sometimes these transactions result in a gain for the fund, so the cost is shown as a negative value (e.g. -0.54%).`,
    incidentalCostTitle: 'Incidental cost',
    incidentalCostDesc: 'This cost is estimated by the fund manager. Incidental costs primarily include performance fees, where applicable.',
    platformFeeTitle: 'AUM fee',
    platformFeeDesc: 'This is the fee charged by Emma for providing access to your account, tools, and services.',
};
export const fundingAccountToolTipStrings = {
    fundingAccountTitle: 'Funding account',
    fundingAccountDesc: 'This account is used for deposits and withdrawals for Emma Invest and Emma pots. If you’d like to change your funding account, please contact our support team.',
};
export const zeroCurrencyAmount = {
    amount: 0,
    currency: 'GBP',
};
export const fundThemeInfoMap = {
    Tech: {
        title: 'Tech',
        width: rem(67),
        colorKey: 'tech',
        icon: (color) => <IconTechBold color={color} alt/>,
    },
    ESG: {
        title: 'ESG',
        width: rem(61),
        colorKey: 'esg',
        icon: (color) => <IconLeaf color={color}/>,
    },
    Bonds: {
        title: 'Bonds',
        width: rem(79),
        colorKey: 'bonds',
        icon: (color) => <IconCoins color={color} alt/>,
    },
    'Consumer goods': {
        title: 'Consumer goods',
        width: rem(156),
        colorKey: 'consumerGoods',
        icon: (color) => <IconShoppingBags color={color}/>,
    },
    Healthcare: {
        title: 'Healthcare',
        width: rem(112),
        colorKey: 'healthcare',
        icon: (color) => <IconHeartBeat color={color}/>,
    },
    // Defense: {
    //   title: 'Defense',
    //   width: rem(92),
    //   colorKey: 'defense',
    //   icon: (color: ColorValue) => <IconShieldDone color={color} />,
    // },
    Commodities: {
        title: 'Commodities',
        width: rem(135),
        colorKey: 'commodities',
        icon: (color) => <IconMine color={color}/>,
    },
    'Real estate': {
        title: 'Real estate',
        width: rem(112),
        colorKey: 'realEstate',
        icon: (color) => <IconHomeBold color={color}/>,
    },
    Sharia: {
        title: 'Sharia',
        width: rem(78),
        colorKey: 'sharia',
        icon: (color) => <IconMoonBold color={color}/>,
    },
};
export const fundGeoInfoMap = {
    UK: {
        title: 'UK',
        width: rem(54),
        subTitle: 'Focused on\nBritish companies',
        imgUrl: require('./img/geo-uk.png'),
    },
    US: {
        title: 'US',
        width: rem(52),
        subTitle: 'Leading US\ncompanies',
        imgUrl: require('./img/geo-US.png'),
    },
    World: {
        title: 'World',
        width: rem(76),
        subTitle: 'Invest in\nworldwide markets',
        imgUrl: require('./img/geo-world.png'),
    },
    Other: {
        title: 'Other',
        width: rem(74),
        subTitle: 'Europe and other\nmarkets',
        imgUrl: require('./img/geo-others.png'),
    },
};
export const IsaAllowanceNumeric = 20_000;
export const IsaAllowanceFormatted = '£20,000';
export const JisaAllowanceNumeric = 9_000;
export const JisaAllowanceFormatted = '£9,000';
export const AccountTypeDisplay = {
    GIA: 'GIA',
    ISA: 'S&S ISA',
    JISA: 'JISA',
};
export const AccountTypeDisplayLong = {
    GIA: 'General Investments',
    ISA: 'Stocks & Shares ISA',
    JISA: 'Junior ISA',
};
export const AccountTypeDescription = {
    ISA: (<Text TextThin-14 Secondary>
      Grow your money tax-efficiently, up to{' '}
      <Text NumbersThin-14 Secondary>
        {IsaAllowanceFormatted}
      </Text>{' '}
      a year
    </Text>),
    GIA: 'Invest beyond your ISA allowance with no annual limit',
    JISA: (<Text TextThin-14 Secondary>
      Give your child a flying start and invest up to{' '}
      <Text NumbersThin-14 Secondary>
        {JisaAllowanceFormatted}
      </Text>{' '}
      a year tax-free
    </Text>),
};
export const ProductTransferDisplayLongMap = {
    ISA: 'Stocks & Shares ISA',
    JISA: 'child’s Junior ISA',
};
export const ProductTransferDisplayMap = {
    ISA: 'S&S ISA',
    JISA: 'child’s JISA',
};
export const ProductTransferDisplayShortMap = {
    ISA: 'ISA',
    JISA: 'child’s JISA',
};
export const JisaAllowanceDescription = [
    `Each tax year, you can pay into one Junior Stocks & Shares ISA for an eligible child. You can invest up to £9,000.`,
    'Unlike an adult ISA, a Junior ISA is not flexible. Once money is contributed, it can’t be withdrawn until the child turns 18. At that point, the account converts to an adult ISA and the child gains full control of it.',
    'Any money kept within a Junior ISA grows tax-free. The tax year runs from 6 April to 5 April.',
];
export const IsaAllowanceDescription = [
    <Text TextThin-14 Secondary key="1">
    You can pay up to{' '}
    <Text NumbersThin-14 Secondary>
      {IsaAllowanceFormatted}
    </Text>{' '}
    into ISAs each tax year (6 April to 5 April), in one ISA or split across several. Any interest or growth is
    tax-free.
  </Text>,
    `Our Cash ISA and Stocks & Shares ISA are both flexible. If you take money out, you can put it back into the same ISA in the same tax year without using more of your allowance. If you put it into a different ISA, it counts as a new payment and uses your allowance.`,
    `If you’ve paid into ISAs with other providers this tax year, you may have less allowance left than shown here.`,
];
export const IsaNoAllowanceLeftDescription = (<Text TextThin-14 Secondary>
    You’ve already deposited{' '}
    <Text NumbersThin-14 Secondary>
      {IsaAllowanceFormatted}
    </Text>{' '}
    this tax year. You can add more once the new tax year begins.
  </Text>);
export const MAXIMUM_INVEST_AMOUNT = 3000;
export const MINIMUM_AUTOINVEST_AMOUNT = 100;
export const INITIAL_INVESTMENT_AMOUNTS = [50, 100, 250];
export const MONTHLY_INVESTMENT_AMOUNTS = [100, 250, 500];
export const transferProductString = {
    CASH: 'Cash',
    STOCKS_AND_SHARES: 'Stocks & Shares',
};
export const INVEST_MAXIMUM_DD_DEPOSIT_LIMIT = 3000;
export const INVEST_MAXIMUM_OB_DEPOSIT_LIMIT = 1000000;
